import axios, {
  AxiosRequestConfig,
  AxiosError,
  Method,
  AxiosHeaders,
} from "axios";
import { myLocalStorage } from "../components/StorageHelper";
import {
  checkIfTokenExpired,
  decodeKeycloakToken,
  refreshAccessToken,
  sendTokenToBackend,
} from "./tokenHelpers";
import { logOut } from "../services/Auth";
import { clearLocalStorageOnLogout } from "../services/Helpers";

export interface ApiRequestOptions {
  method: Method;
  url: string;
  data?: any;
  params?: Record<string, any>;
  headers?: Record<string, string>;
  timeout?: number;
  signal?: AbortSignal;
}

const api = axios.create({});
const nativeAxiosRequest = axios.request.bind(axios);

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token!);
  });
  failedQueue = [];
};


api.interceptors.request.use(
  (request) => {
    const access_token = myLocalStorage.getItem("access_token");
    if (access_token)
      request.headers!["Authorization"] = `Bearer ${access_token}`;
    request.headers!["Referrer-Policy"] = "strict-origin-when-cross-origin";
    return request;
  },
  (error) => Promise.reject(error)
);


api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    if (originalRequest?.url?.includes("/logout")) {
      return Promise.reject(error);
    }

    if (!error.response || error.response.status !== 401) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      return Promise.reject(error);
    }
    originalRequest._retry = true;

    const stored_refresh_token = myLocalStorage.getItem("refresh_token");

    if (!stored_refresh_token || checkIfTokenExpired(stored_refresh_token)) {
      await logOutDueToTokenExpire();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            resolve(api(originalRequest));
          },
          reject,
        });
      });
    }

    isRefreshing = true;

    try {
      const realm = myLocalStorage.getItem("realm");
      const email = myLocalStorage.getItem("email");
      let role = myLocalStorage.getItem("role");
      if (myLocalStorage.getItem("azuresso")) role = "TENANT_USER";

      const { access_token, refresh_token } = await refreshAccessToken();

      if (!access_token) {
        throw new Error("Token refresh failed");
      }

      myLocalStorage.setItem("access_token", access_token);
      myLocalStorage.setItem("refresh_token", refresh_token);

      const decode = decodeKeycloakToken(access_token);
      const exp = decode?.exp;
      await sendTokenToBackend(realm, email, role, access_token, exp);

      processQueue(null, access_token);

      originalRequest.headers["Authorization"] = `Bearer ${access_token}`;
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      await logOutDueToTokenExpire();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);


const logOutDueToTokenExpire = async () => {
  const email = myLocalStorage.getItem("email");
  try {
    await logOut(email || "");
  } catch (err) {
    console.error("Logout failed:", err);
  }
  clearLocalStorageOnLogout();
};


export async function apiClient<T = any>(options: ApiRequestOptions): Promise<T> {
  const config: AxiosRequestConfig = {
    method: options.method,
    url: options.url,
    data: options.data,
    params: options.params,
    headers: options.headers || {},
    timeout: options.timeout || 3000000,
    signal: options.signal,
  };

  if (options.url.includes("/logout")) {
    return nativeAxiosRequest(config).then((res) => res.data);
  }

  try {
    const response = await api(config);
    return response.data;
  } catch (error: any) {
    console.error("API Error:", error);
    throw error.response?.data || error.message || "Unknown API Error";
  }
}


const METHODS: Method[] = ["get", "post", "put", "patch", "delete", "head", "options"];

METHODS.forEach((method) => {
  // @ts-ignore
  axios[method] = async (url: string, dataOrConfig?: any, config?: any) => {
    const isGetOrDelete = method === "get" || method === "delete";
    const data = isGetOrDelete ? undefined : dataOrConfig;
    const finalConfig = isGetOrDelete ? dataOrConfig : config;

    const requestOptions: ApiRequestOptions = {
      method: method.toUpperCase() as Method,
      url,
      data,
      params: finalConfig?.params,
      headers: finalConfig?.headers,
      timeout: finalConfig?.timeout,
    };

    return api(requestOptions);
  };
});


axios.request = async (config) => {
  let plainHeaders: Record<string, string> | undefined;

  if (config.headers instanceof AxiosHeaders) {
    plainHeaders = config.headers.toJSON() as Record<string, string>;
  } else if (typeof config.headers === "object" && config.headers !== null) {
    plainHeaders = config.headers as Record<string, string>;
  }

  return api({
    method: config.method!.toUpperCase() as Method,
    url: config.url!,
    data: config.data,
    params: config.params,
    headers: plainHeaders,
  });
};

export default api;
